import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Shield, Crown, Users, Hammer, BookOpen, Plus, Search, LogOut,
  UserPlus, Check, X, Trash2, Trophy, Swords, Gem, ChevronUp, ChevronLeft, ChevronRight, Circle, ArrowLeft,
  ShieldAlert, Clock, Ticket, MessagesSquare,
} from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader, Empty, btn } from "@/components/Page";
import { AllianceBanner, AllianceSigil } from "@/lib/cosmetics";
import { RankBadge, RankManager } from "@/components/alliance/RankManager";
import ConfirmDialog from "@/components/ConfirmDialog";
import { Treasury } from "@/components/alliance/Treasury";
import { TopGladiators } from "@/components/alliance/TopGladiators";
import { Roster } from "@/components/alliance/Members";
import { CustomizeCTA } from "@/components/alliance/CustomizeCTA";
import { AllianceAnnouncements } from "@/components/alliance/Announcements";

// Custom Alliance Icon
const AllianceIcon = ({ size = 24, className = "" }) => (
  <img 
    src="/alliance-icon.png" 
    alt="Alliance" 
    className={`inline-block ${className}`}
    style={{ 
      width: size * 1.2,
      height: size * 1.2,
      objectFit: 'contain'
    }}
  />
);

const FORGE_MAX = 5, LIB_MAX = 5, SIZE_MAX = 15;

export default function Alliance() {
  const { allianceId } = useParams();
  if (allianceId) return <AlliancePublic id={allianceId} />;
  return <MyAlliance />;
}

/* ---------------- Public read-only view of any alliance ---------------- */
function AlliancePublic({ id }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [a, setA] = useState(null);
  const [adminChat, setAdminChat] = useState(null);
  const [mine, setMine] = useState(null);

  const load = useCallback(() => api.get(`/alliances/${id}`).then((r) => setA(r.data.alliance)).catch(() => setA(false)), [id]);
  const loadMine = useCallback(() => api.get("/alliance").then((r) => setMine(r.data.alliance)).catch(() => setMine(null)), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadMine(); }, [loadMine]);
  useEffect(() => {
    if (user?.is_admin) {
      api.get(`/alliances/${id}/chat`).then((r) => setAdminChat(r.data.messages || [])).catch(() => setAdminChat([]));
    }
  }, [id, user]);

  const request = async () => {
    try { await api.post(`/alliances/${id}/request`); toast.success("Join request sent"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const isMyRival = mine && mine.rival_id === id;
  const canRival = mine && mine.is_leader && mine.id !== id;
  const toggleRival = async () => {
    try {
      await api.post("/alliance/rival", { alliance_id: isMyRival ? null : id });
      toast.success(isMyRival ? "Rivalry called off" : `${a.name} marked as your rival`);
      loadMine();
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (a === null) return <Loader label="Reading the alliance charter…" />;
  if (a === false) return (
    <div>
      <PageHeader title="Alliance" icon={AllianceIcon} />
      <Empty label="This alliance no longer exists." />
    </div>
  );

  return (
    <div>
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-zinc-500 hover:text-amber-400 text-sm mb-4 transition-colors"><ArrowLeft size={15} /> Back</button>
      <div className="relative overflow-hidden border border-amber-700/40 bg-gradient-to-r from-amber-950/20 to-zinc-950 p-5 flex flex-wrap items-center gap-5 mb-6">
        {a.banner && <AllianceBanner id={a.banner} className="absolute inset-0 opacity-30" />}
        <div className="relative flex items-center gap-3">
          {a.sigil
            ? <AllianceSigil id={a.sigil} size={48} title={a.name} />
            : <div className="w-12 h-12 rounded-sm bg-amber-900/30 border border-amber-700/40 flex items-center justify-center"><Shield className="text-amber-400" size={24} /></div>}
          <div>
            <h2 className="font-cinzel text-2xl text-amber-200 tracking-wide" data-testid="alliance-name">{a.name} {a.tag && <span className="text-zinc-600 text-base">[{a.tag}]</span>}</h2>
            <div className="text-xs text-zinc-500 flex items-center gap-1.5"><Crown size={12} className="text-amber-500" /> {a.leader_name}</div>
            <AllianceTrophies trophies={a.trophies} />
          </div>
        </div>
        <div className="relative flex items-center gap-6 ml-auto">
          <Stat label="Members" value={`${a.member_count}/${a.max_members}`} icon={Users} />
          {isMyRival && <span data-testid="rival-flag" className="text-[10px] uppercase tracking-wider text-red-400 border border-red-500/40 bg-red-950/30 px-2 py-1 rounded-sm font-cinzel">Your Rival</span>}
          {canRival && (
            <button data-testid="toggle-rival-btn" onClick={toggleRival}
              className={`py-2 px-3 rounded-sm text-xs font-cinzel uppercase tracking-wide transition-colors flex items-center gap-1.5 border ${isMyRival ? "border-zinc-700 text-zinc-300 hover:border-zinc-500" : "border-red-700/50 text-red-300 hover:bg-red-950/30"}`}>
              <Swords size={14} /> {isMyRival ? "Remove Rival" : "Mark as Rival"}
            </button>
          )}
        </div>
      </div>

      <div className="mb-6"><AllianceAnnouncements a={a} reload={load} canPost={!!a.can_announce} /></div>

      <div className="grid sm:grid-cols-2 gap-6">
        <section className="border border-amber-900/30 bg-zinc-950 p-5">
          <h3 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm mb-3 flex items-center gap-2"><Users size={16} /> Roster</h3>
          <div className="space-y-2" data-testid="alliance-members">
            {a.members.map((m) => (
              <div key={m.user_id} className="border-b border-zinc-900 pb-2">
                <div className="flex items-center gap-2 text-sm">
                  {m.is_leader ? <Crown size={14} className="text-amber-500 shrink-0" /> : <Circle className="text-zinc-700 shrink-0" size={14} />}
                  <button onClick={() => navigate(`/profile/${m.user_id}`)} className="text-zinc-100 hover:text-amber-400">{m.name}</button>
                  {m.vip && <Crown size={11} className="text-amber-400" />}
                  <RankBadge rank={m.rank} />
                  <span className="text-[11px] text-zinc-600 ml-auto">{m.active_gladiators} glads</span>
                </div>
                {a.is_member && (
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 pl-6 text-[11px] text-zinc-600">
                    <span data-testid={`public-member-lastlogin-${m.user_id}`} className="flex items-center gap-1" title="Last login">
                      <Clock size={11} className="text-zinc-700" /> Last login: <span className="text-zinc-500">{m.last_login ? new Date(m.last_login).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "never"}</span>
                    </span>
                    <span data-testid={`public-member-contribution-${m.user_id}`} className="flex items-center gap-1" title="This month’s ranking contribution from this lanista’s top five gladiators">
                      <Trophy size={11} className="text-amber-600/70" /> <span className="text-amber-400/80 tabular-nums">{m.season_points ?? 0}</span> pts this month <span data-testid={`public-member-counted-gladiators-${m.user_id}`} className="text-zinc-700">({m.season_counted_gladiators ?? 0}/5)</span>
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
        <section className="border border-amber-900/30 bg-zinc-950 p-5">
          <h3 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm mb-3 flex items-center gap-2"><Gem size={16} /> Perks</h3>
          <ul className="space-y-2 text-sm text-zinc-300">
            <li className="flex items-center gap-2"><Hammer size={15} className="text-amber-500/70" /> Forge: {a.forge_tier > 0 ? `-${a.repair_discount}% repair cost` : "not built"}</li>
            <li className="flex items-center gap-2"><BookOpen size={15} className="text-amber-500/70" /> Library: {a.library_tier > 0 ? `+${a.scout_bonus}% scouting` : "not built"}</li>
          </ul>
          {(a.free_entry_brackets || []).length > 0 && (
            <div className="mt-4 pt-4 border-t border-zinc-800" data-testid="public-free-entries">
              <h4 className="font-cinzel text-sky-300 uppercase tracking-wide text-xs flex items-center gap-2 mb-2"><Trophy size={14} /> Free Tournament Entries</h4>
              <div className="flex flex-wrap gap-1.5">
                {[["beginners", "Beginners"], ["bronze", "Bronze"], ["silver", "Silver"], ["gold", "Gold"], ["elite", "Elite"], ["legendary", "Legendary"], ["par_aequa", "Par Aequa"]]
                  .filter(([key]) => (a.free_entry_brackets || []).includes(key))
                  .map(([key, name]) => (
                    <span key={key} data-testid={`public-free-entry-${key}`}
                      className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider text-emerald-300 border border-emerald-700/50 bg-emerald-950/20 px-2 py-1 rounded-sm">
                      <Check size={11} /> {name}
                    </span>
                  ))}
              </div>
            </div>
          )}
          {a.can_request && (
            <button data-testid="public-request-join" onClick={request} className={btn("primary") + " mt-5 py-2 px-4 text-sm"}>Request to Join</button>
          )}
          {a.is_member && <p className="text-xs text-amber-400/70 mt-5">You are a member of this alliance.</p>}
        </section>
      </div>

      {user?.is_admin && (
        <section data-testid="admin-alliance-chat" className="border border-red-900/40 bg-zinc-950 p-5 mt-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-cinzel text-red-300/90 uppercase tracking-wide text-sm flex items-center gap-2">
              <ShieldAlert size={16} /> War Room <span className="text-zinc-600 normal-case tracking-normal text-xs">(admin view — read only)</span>
            </h3>
            <button 
              onClick={() => navigate(`/alliance/${id}/warroom`)}
              className="text-sm px-3 py-1.5 border border-red-700/50 text-red-300 hover:bg-red-950/30 rounded-sm flex items-center gap-1.5 transition-colors"
            >
              <MessagesSquare size={14} /> View Message Board
            </button>
          </div>
          {adminChat === null ? (
            <p className="text-xs text-zinc-600">Loading chat…</p>
          ) : adminChat.length === 0 ? (
            <p className="text-xs text-zinc-600">No messages in this alliance's war room yet.</p>
          ) : (
            <div className="h-64 overflow-y-auto space-y-2 pr-1" data-testid="admin-alliance-chat-messages">
              {adminChat.map((m) => (
                <div key={m.id} className="text-sm">
                  <span className={m.is_leader ? "text-amber-400 font-semibold" : "text-zinc-300 font-semibold"}>{m.name}</span>
                  {m.is_leader && <Crown size={11} className="inline ml-1 mb-0.5 text-amber-500" />}
                  <span className="text-zinc-500 text-[11px] ml-2">{new Date(m.created_at).toLocaleString()}</span>
                  <p className="text-zinc-300">{m.text}</p>
                </div>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function MyAlliance() {
  const [data, setData] = useState(null);
  const [invites, setInvites] = useState([]);

  const load = useCallback(() => {
    api.get("/alliance").then((r) => setData(r.data)).catch((e) => { toast.error(apiErr(e)); setData({ alliance: null }); });
    api.get("/alliance/invites").then((r) => setInvites(r.data.invites)).catch(() => setInvites([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (data === null) return <Loader label="Gathering the alliance…" />;

  return (
    <div>
      <PageHeader title="Alliances" subtitle="Band together, pool your fights, and unlock shared perks" icon={AllianceIcon} />
      {data.alliance
        ? <AllianceHome a={data.alliance} reload={load} />
        : <NoAlliance canCreate={data.can_create} pending={data.pending_request_ids || []} invites={invites} reload={load} />}
    </div>
  );
}

/* ---------------- No alliance: invites + browse + create ---------------- */
function NoAlliance({ canCreate, pending, invites, reload }) {
  const [name, setName] = useState("");
  const [tag, setTag] = useState("");
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (name.trim().length < 3) return toast.error("Name must be at least 3 characters");
    if (tag.trim().length < 2) return toast.error("An alliance tag is required (2–5 characters)");
    setBusy(true);
    try { await api.post("/alliance", { name: name.trim(), tag: tag.trim() }); toast.success("Alliance founded!"); reload(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const act = async (fn) => { try { await fn(); reload(); } catch (e) { toast.error(apiErr(e)); } };
  const acceptInvite = (id) => act(() => api.post(`/alliance/invites/${id}/accept`).then(() => toast.success("Joined alliance!")));
  const declineInvite = (id) => act(() => api.post(`/alliance/invites/${id}/decline`));

  return (
    <div className="space-y-8">
      {invites.length > 0 && (
        <section data-testid="alliance-invites">
          <h2 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm mb-3 flex items-center gap-2"><UserPlus size={16} /> Your Invitations</h2>
          <div className="space-y-2">
            {invites.map((inv) => (
              <div key={inv.id} data-testid={`invite-${inv.id}`} className="flex items-center gap-3 border border-amber-900/40 bg-amber-950/10 px-4 py-3">
                <Shield size={16} className="text-amber-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-zinc-100 text-sm truncate"><span className="font-cinzel text-amber-200">{inv.alliance_name}</span> invited you</div>
                  <div className="text-[11px] text-zinc-500">{inv.member_count}/{inv.max_members} members · from {inv.from_name}</div>
                </div>
                <button data-testid={`invite-accept-${inv.id}`} onClick={() => acceptInvite(inv.id)} className={btn("primary") + " py-1.5 px-3 flex items-center gap-1"}><Check size={14} /> Accept</button>
                <button data-testid={`invite-decline-${inv.id}`} onClick={() => declineInvite(inv.id)} className={btn("ghost") + " py-1.5 px-3"}><X size={14} /></button>
              </div>
            ))}
          </div>
        </section>
      )}

      <section>
        <div className="border border-amber-900/30 bg-zinc-950 p-5">
          <h2 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm mb-1 flex items-center gap-2"><Plus size={16} /> Found an Alliance</h2>
          {canCreate ? (
            <>
              <p className="text-zinc-500 text-sm mb-4">As a VIP lanista you may establish a new alliance and lead it. Starts with 3 member slots.</p>
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[200px]"><label className="block text-[11px] uppercase tracking-widest text-zinc-500 mb-1">Alliance name</label>
                  <input data-testid="alliance-name-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={32} placeholder="e.g. The Iron Wolves"
                    className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2.5 text-zinc-100 text-sm focus:border-amber-600 outline-none" /></div>
                <div className="w-28"><label className="block text-[11px] uppercase tracking-widest text-zinc-500 mb-1">Tag <span className="text-amber-500">*</span></label>
                  <input data-testid="alliance-tag-input" value={tag} onChange={(e) => setTag(e.target.value)} maxLength={5} placeholder="WOLF" required
                    className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2.5 text-zinc-100 text-sm uppercase focus:border-amber-600 outline-none" /></div>
                <button data-testid="alliance-create-btn" disabled={busy || name.trim().length < 3 || tag.trim().length < 2} onClick={create} className={btn("primary") + " py-2.5 disabled:opacity-50"}>Found</button>
              </div>
            </>
          ) : (
            <div className="flex items-center gap-2 text-sm text-zinc-400 mt-2">
              <Crown size={15} className="text-amber-500" /> You must be a <span className="text-amber-300">VIP member</span> to found an alliance — but anyone can join one below.
            </div>
          )}
        </div>
      </section>

      <AllianceBrowser canJoin pending={pending} reload={reload} />
    </div>
  );
}

/* ---------------- Reusable alliance browser (usable in or out of an alliance) ---------------- */
function AllianceBrowser({ canJoin = false, pending = [], reload }) {
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const PER_PAGE = 5;

  const loadList = useCallback(() => api.get(`/alliances?q=${encodeURIComponent(q)}`).then((r) => setList(r.data.alliances)).catch(() => setList([])), [q]);
  useEffect(() => { const t = setTimeout(loadList, 250); return () => clearTimeout(t); }, [loadList]);
  useEffect(() => { setPage(1); }, [q]);

  const request = async (id) => {
    try { await api.post(`/alliances/${id}/request`); toast.success("Join request sent"); reload && reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const total = list ? list.length : 0;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const pageItems = list ? list.slice((page - 1) * PER_PAGE, page * PER_PAGE) : [];

  return (
    <section data-testid="alliance-browser">
      <h2 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm mb-3 flex items-center gap-2"><Users size={16} /> Browse Alliances</h2>
      <div className="relative mb-3 max-w-md">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <input data-testid="alliance-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search alliances…"
          className="w-full bg-zinc-900 border border-zinc-800 pl-9 pr-3 py-2.5 text-zinc-100 text-sm focus:border-amber-600 outline-none" />
      </div>
      {list === null ? <Loader /> : list.length === 0 ? <Empty label="No alliances yet. Be the first to found one!" /> : (
        <>
        <div className="grid sm:grid-cols-2 gap-3" data-testid="alliance-list">
          {pageItems.map((a) => {
            const requested = pending.includes(a.id);
            return (
              <div key={a.id} data-testid={`alliance-card-${a.id}`} onClick={() => navigate(`/alliance/${a.id}`)}
                className="border border-amber-900/30 bg-zinc-950 p-4 cursor-pointer hover:border-amber-700/50 hover:-translate-y-0.5 transition-all">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex items-center gap-2">
                    {a.sigil ? <AllianceSigil id={a.sigil} size={28} /> : null}
                    <div className="min-w-0">
                      <h3 className="font-cinzel text-amber-200 tracking-wide truncate">{a.name} {a.tag && <span className="text-zinc-600 text-xs">[{a.tag}]</span>}</h3>
                      <div className="text-[11px] text-zinc-500 mt-0.5">Led by {a.leader_name}</div>
                    </div>
                  </div>
                  <span className="text-xs text-zinc-400 shrink-0">{a.member_count}/{a.max_members}</span>
                </div>
                <div className="flex items-center gap-3 mt-3 text-[11px] text-zinc-500">
                  {a.points_total != null && <span className="flex items-center gap-1"><Trophy size={12} className="text-amber-500/70" /> {a.points_total} pts</span>}
                  {a.forge_tier > 0 && <span className="flex items-center gap-1"><Hammer size={12} className="text-amber-500/70" /> -{a.repair_discount}%</span>}
                  {a.library_tier > 0 && <span className="flex items-center gap-1"><BookOpen size={12} className="text-amber-500/70" /> +{a.scout_bonus}%</span>}
                  {(a.free_entry_brackets || []).length > 0 && <span data-testid={`alliance-card-free-entries-${a.id}`} className="flex items-center gap-1 text-sky-400/80"><Ticket size={12} className="text-sky-400/70" /> {a.free_entry_brackets.length} free {a.free_entry_brackets.length === 1 ? "entry" : "entries"}</span>}
                </div>
                {canJoin && (
                  <div className="mt-3">
                    {a.is_full ? <span className="text-xs text-zinc-600">Full</span>
                      : requested ? <span data-testid={`requested-${a.id}`} className="text-xs text-amber-400/80">Request pending…</span>
                      : <button data-testid={`request-join-${a.id}`} onClick={(e) => { e.stopPropagation(); request(a.id); }} className={btn("ghost") + " py-1.5 px-3 text-xs"}>Request to Join</button>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between gap-3 mt-4 text-sm" data-testid="alliance-browse-pagination">
            <span className="text-[11px] uppercase tracking-widest text-zinc-500">{total} alliances</span>
            <div className="flex items-center gap-2">
              <button data-testid="alliance-browse-prev" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="flex items-center gap-1 px-3 py-1.5 border border-amber-900/40 text-zinc-300 hover:border-amber-600 hover:text-amber-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"><ChevronLeft size={15} /> Prev</button>
              <span className="text-[11px] uppercase tracking-widest text-zinc-500 tabular-nums">Page {page} of {totalPages}</span>
              <button data-testid="alliance-browse-next" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="flex items-center gap-1 px-3 py-1.5 border border-amber-900/40 text-zinc-300 hover:border-amber-600 hover:text-amber-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">Next <ChevronRight size={15} /></button>
            </div>
          </div>
        )}
        </>
      )}
    </section>
  );
}

/* ---------------- In an alliance ---------------- */
function AllianceHome({ a, reload }) {
  const navigate = useNavigate();
  const isLeader = a.is_leader;
  const nextPoint = 20 - (a.total_fights % 20);

  const act = async (fn, ok) => { try { await fn(); if (ok) toast.success(ok); reload(); } catch (e) { toast.error(apiErr(e)); } };
  const leave = () => setConfirm({
    title: "Leave alliance",
    message: isLeader
      ? "Leave your alliance? Leadership will pass to the longest-standing member, or it will disband if you're the last one."
      : "Are you sure you want to leave this alliance?",
    confirmLabel: "Leave", danger: true,
    onConfirm: () => { setConfirm(null); act(() => api.post("/alliance/leave"), "You left the alliance"); },
  });
  const disband = () => setConfirm({
    title: "Disband alliance",
    message: `Permanently DISBAND ${a.name}? All members will be removed and upgrades lost. This cannot be undone.`,
    confirmLabel: "Disband", danger: true,
    onConfirm: () => { setConfirm(null); act(() => api.post("/alliance/disband"), "Alliance disbanded"); },
  });
  const kick = (uid, name) => setConfirm({
    title: `Remove ${name}`,
    message: `Remove ${name} from the alliance?`,
    confirmLabel: "Remove", danger: true,
    onConfirm: () => { setConfirm(null); act(() => api.post("/alliance/kick", { user_id: uid }), `${name} removed`); },
  });
  const upgrade = (type) => act(() => api.post("/alliance/upgrade", { type }), "Upgrade purchased!");
  const unlockTourney = (bracket, name) => act(() => api.post("/alliance/unlock-tourney", { bracket }), `Free ${name} entries unlocked for all members!`);
  const approve = (id) => act(() => api.post(`/alliance/requests/${id}/approve`), "Member approved");
  const reject = (id) => act(() => api.post(`/alliance/requests/${id}/reject`));
  const [rankFor, setRankFor] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const canSpend = a.can_spend_points;
  const canManage = a.can_manage_invites;

  return (
    <div className="space-y-6">
      {/* Header card */}
      <div className="relative overflow-hidden border border-amber-700/40 bg-gradient-to-r from-amber-950/20 to-zinc-950 p-5 flex flex-wrap items-center gap-5">
        {a.banner && <AllianceBanner id={a.banner} className="absolute inset-0 opacity-30" />}
        <div className="relative flex items-center gap-3">
          {a.sigil
            ? <AllianceSigil id={a.sigil} size={48} title={a.name} />
            : <div className="w-12 h-12 rounded-sm bg-amber-900/30 border border-amber-700/40 flex items-center justify-center"><Shield className="text-amber-400" size={24} /></div>}
          <div>
            <h2 className="font-cinzel text-2xl text-amber-200 tracking-wide" data-testid="alliance-name">{a.name} {a.tag && <span className="text-zinc-600 text-base">[{a.tag}]</span>}</h2>
            <div className="text-xs text-zinc-500 flex items-center gap-1.5"><Crown size={12} className="text-amber-500" /> {a.leader_name} {isLeader && <span className="text-amber-400/70">(you)</span>}</div>
            <AllianceTrophies trophies={a.trophies} />
          </div>
        </div>
        <div className="relative flex items-center gap-6 ml-auto">
          <Stat label="Members" value={`${a.member_count}/${a.max_members}`} icon={Users} />
          <Stat label="Alliance Pts" value={a.points_available} icon={Trophy} testid="alliance-points" />
          <Stat label="Total Fights" value={a.total_fights} icon={Swords} />
        </div>
      </div>

      <div className="text-[11px] text-zinc-500 -mt-2">Earned <span className="text-amber-400">{a.points_total}</span> points lifetime · {nextPoint} more PvP fight{nextPoint === 1 ? "" : "s"} to the next point · {a.points_spent} spent</div>

      {/* War Room and Armory Links */}
      <div className="grid grid-cols-2 gap-4 mb-6 mt-6">
        <button
          onClick={() => navigate("/alliance/war-room")}
          className="border border-amber-900/30 bg-zinc-950 hover:bg-zinc-900 p-4 transition-colors flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            <ShieldAlert size={20} className="text-amber-500" />
            <div className="text-left">
              <div className="font-cinzel text-amber-300 text-sm uppercase tracking-wide">War Room</div>
              <div className="text-xs text-zinc-500">Chat & boards</div>
            </div>
          </div>
          <ChevronRight size={18} className="text-zinc-600" />
        </button>

        <button
          onClick={() => navigate("/alliance/armory")}
          className="border border-amber-900/30 bg-zinc-950 hover:bg-zinc-900 p-4 transition-colors flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            <div className="text-left">
              <div className="font-cinzel text-amber-300 text-sm uppercase tracking-wide">Armory</div>
              <div className="text-xs text-zinc-500">Gear & items</div>
            </div>
          </div>
          <ChevronRight size={18} className="text-zinc-600" />
        </button>
      </div>

      <AllianceAnnouncements a={a} reload={reload} canPost={!!a.can_announce} />

      {/* Top 5 Gladiators */}
      <TopGladiators />

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Members */}
        <Roster a={a} isLeader={isLeader} canManage={canManage} onManageRank={setRankFor} onKick={kick} reload={reload} />

        {/* Upgrades */}
        <section className="border border-amber-900/30 bg-zinc-950 p-5">
          <h3 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm mb-3 flex items-center gap-2"><Gem size={16} /> Hall Upgrades {canSpend ? "" : <span className="text-zinc-600 normal-case tracking-normal text-xs">(officers only)</span>}</h3>
          <div className="space-y-3">
            <Upgrade testid="upg-forge" icon={Hammer} title="Forge" tier={a.forge_tier} max={FORGE_MAX} cost={a.forge_next_cost}
              effect={a.forge_tier > 0 ? `-${a.repair_discount}% repair cost` : "Cheaper gear repairs for all members"}
              next={a.forge_tier === 0 ? "Unlock: -10% repair cost" : `Upgrade to -${(a.forge_tier + 1) * 10}%`}
              canBuy={canSpend && a.forge_tier < FORGE_MAX && a.points_available >= (a.forge_next_cost || 0)} maxed={a.forge_tier >= FORGE_MAX} onBuy={() => upgrade("forge")} />
            <Upgrade testid="upg-library" icon={BookOpen} title="Library" tier={a.library_tier} max={LIB_MAX} cost={a.library_next_cost}
              effect={a.library_tier > 0 ? `+${a.scout_bonus}% scouting speed` : "Faster scouting for all members"}
              next={a.library_tier === 0 ? "Unlock: +5% scouting" : `Upgrade to +${(a.library_tier + 1) * 5}%`}
              canBuy={canSpend && a.library_tier < LIB_MAX && a.points_available >= (a.library_next_cost || 0)} maxed={a.library_tier >= LIB_MAX} onBuy={() => upgrade("library")} />
            <Upgrade testid="upg-size" icon={Users} title="Alliance Size" tier={a.max_members - 3} max={(a.size_cap || SIZE_MAX) - 3} cost={1}
              effect={`${a.max_members} member slots`} next={`+1 slot (to ${a.max_members + 1})`}
              canBuy={canSpend && a.max_members < (a.size_cap || SIZE_MAX) && a.points_available >= 1} maxed={a.max_members >= (a.size_cap || SIZE_MAX)} onBuy={() => upgrade("size")} unit="member" />
          </div>

          {/* Free tournament entries — permanent, paid from treasury gold */}
          <div className="mt-5 pt-4 border-t border-zinc-800" data-testid="tourney-unlocks">
            <div className="flex items-center justify-between mb-1">
              <h4 className="font-cinzel text-sky-300 uppercase tracking-wide text-xs flex items-center gap-2"><Trophy size={14} /> Free Tournament Entries</h4>
              <span className="text-[11px] text-zinc-500">Treasury: <span className="text-amber-300 tabular-nums">{(a.treasury_gold || 0).toLocaleString()}g</span></span>
            </div>
            <p className="text-[11px] text-zinc-500 mb-3">Permanently waive a bracket's entry fee for <span className="text-zinc-300">every member</span> — paid once from the treasury {a.can_treasury ? "" : <span className="text-zinc-600">(treasury officers only)</span>}.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {[["beginners", "Beginners"], ["bronze", "Bronze"], ["silver", "Silver"], ["gold", "Gold"], ["elite", "Elite"], ["legendary", "Legendary"], ["par_aequa", "Par Aequa"]].map(([key, name]) => {
                const isUnlocked = new Set(a.free_entry_brackets || []).has(key);
                const cost = (a.tourney_unlock_costs || {})[key] || 0;
                const affordable = (a.treasury_gold || 0) >= cost;
                return (
                  <div key={key} data-testid={`tourney-unlock-${key}`}
                    className={`border p-2.5 rounded-sm flex items-center justify-between gap-2 ${isUnlocked ? "border-emerald-700/50 bg-emerald-950/10" : "border-zinc-800 bg-zinc-950"}`}>
                    <div className="min-w-0">
                      <div className="font-cinzel text-sm text-zinc-100">{name}</div>
                      {isUnlocked
                        ? <div className="text-[10px] uppercase tracking-widest text-emerald-400 flex items-center gap-1"><Check size={11} /> Unlocked</div>
                        : <div className="text-[11px] text-amber-300 tabular-nums">{cost.toLocaleString()}g</div>}
                    </div>
                    {!isUnlocked && (
                      <button data-testid={`tourney-unlock-btn-${key}`} disabled={!a.can_treasury || !affordable}
                        onClick={() => unlockTourney(key, name)}
                        className={btn(affordable && a.can_treasury ? "primary" : "ghost") + " py-1 px-2.5 text-xs shrink-0 disabled:opacity-40"}>
                        {a.can_treasury ? (affordable ? "Unlock" : "Low") : "—"}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      </div>

      {/* Pending requests (leader/magister) */}
      {canManage && a.pending_requests && a.pending_requests.length > 0 && (
        <section className="border border-amber-900/30 bg-zinc-950 p-5" data-testid="pending-requests">
          <h3 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm mb-3 flex items-center gap-2"><UserPlus size={16} /> Join Requests</h3>
          <div className="space-y-2">
            {a.pending_requests.map((r) => (
              <div key={r.id} className="flex items-center gap-3 text-sm border-b border-zinc-900 pb-2">
                <span className="text-zinc-100">{r.user_name}</span>
                <button data-testid={`approve-${r.id}`} onClick={() => approve(r.id)} className={btn("primary") + " py-1 px-3 ml-auto flex items-center gap-1 text-xs"}><Check size={13} /> Approve</button>
                <button data-testid={`reject-${r.id}`} onClick={() => reject(r.id)} className={btn("ghost") + " py-1 px-3 text-xs"}><X size={13} /></button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Treasury + Customization */}
      <Treasury a={a} reload={reload} />

      <RivalManager a={a} reload={reload} />
      <CustomizeCTA a={a} isLeader={isLeader} />

      {/* Danger zone */}
      <div className="flex items-center gap-3 pt-2">
        <button data-testid="alliance-leave-btn" onClick={leave} className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-amber-300"><LogOut size={15} /> Leave alliance</button>
        {isLeader && <button data-testid="alliance-disband-btn" onClick={disband} className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-red-400 ml-auto"><Trash2 size={15} /> Disband alliance</button>}
      </div>

      {/* Browse other alliances (view-only while you're a member) */}
      <AllianceBrowser canJoin={false} />

      <RankManager member={rankFor} a={a} onClose={() => setRankFor(null)} reload={reload} />
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

const _MEDAL = { 1: "text-amber-400", 2: "text-zinc-300", 3: "text-amber-700" };
const _PLACE = { 1: "Champions", 2: "2nd place", 3: "3rd place" };

function AllianceTrophies({ trophies }) {
  if (!trophies || trophies.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 mt-1" data-testid="alliance-trophies">
      {trophies.slice(0, 6).map((t) => {
        const month = new Date(`${t.season}-01T00:00:00`).toLocaleDateString([], { month: "short", year: "numeric" });
        return (
          <span key={t.season} title={`${_PLACE[t.rank] || `#${t.rank}`} — ${month}`}
            className="inline-flex items-center gap-1 text-[11px] text-zinc-400 border border-zinc-800 bg-zinc-950 px-1.5 py-0.5 rounded-sm">
            <Trophy size={12} className={_MEDAL[t.rank] || "text-zinc-500"} /> {month}
          </span>
        );
      })}
    </div>
  );
}

function Stat({ label, value, icon: Icon, testid }) {  return (
    <div className="text-center" data-testid={testid}>
      <div className="flex items-center justify-center gap-1.5 text-amber-400 font-cinzel text-xl tabular-nums"><Icon size={15} /> {value}</div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">{label}</div>
    </div>
  );
}

function Upgrade({ icon: Icon, title, tier, max, cost, effect, next, canBuy, maxed, onBuy, unit = "tier", testid }) {
  return (
    <div data-testid={testid} className="border border-zinc-800 bg-zinc-950/60 p-3">
      <div className="flex items-center gap-2 mb-1.5">
        <Icon size={16} className="text-amber-400" />
        <span className="font-cinzel text-amber-100 text-sm tracking-wide">{title}</span>
        <span className="text-[10px] text-zinc-600 ml-auto">Lv {tier}/{max}</span>
      </div>
      <div className="flex gap-1 mb-2">
        {Array.from({ length: max }).map((_, i) => (
          <div key={i} className={`h-1.5 flex-1 rounded-sm ${i < tier ? "bg-amber-500" : "bg-zinc-800"}`} />
        ))}
      </div>
      <p className="text-xs text-zinc-400">{effect}</p>
      <div className="flex items-center justify-between mt-2">
        <span className="text-[11px] text-zinc-500">{maxed ? "Fully upgraded" : next}</span>
        {!maxed && (
          <button data-testid={`${testid}-buy`} disabled={!canBuy} onClick={onBuy}
            className="flex items-center gap-1 text-xs px-3 py-1.5 bg-amber-900/20 text-amber-300 border border-amber-700/50 hover:bg-amber-900/40 hover:border-amber-500 disabled:opacity-40 disabled:cursor-not-allowed rounded-sm">
            <ChevronUp size={13} /> {cost} pt{cost > 1 ? "s" : ""}
          </button>
        )}
      </div>
    </div>
  );
}

function RivalManager({ a, reload }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const isLeader = a.is_leader;

  useEffect(() => {
    if (!isLeader || q.trim().length < 2) { setResults(null); return; }
    const t = setTimeout(() => api.get(`/alliances?q=${encodeURIComponent(q)}`)
      .then((r) => setResults((r.data.alliances || []).filter((x) => x.id !== a.id))).catch(() => setResults([])), 250);
    return () => clearTimeout(t);
  }, [q, isLeader, a.id]);

  const setRival = async (allianceId) => {
    setBusy(true);
    try {
      await api.post("/alliance/rival", { alliance_id: allianceId });
      toast.success(allianceId ? "Rival declared!" : "Rivalry called off");
      setQ(""); setResults(null); reload();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <section className="border border-red-900/30 bg-gradient-to-br from-red-950/15 to-zinc-950 p-5" data-testid="rival-manager">
      <h3 className="font-cinzel text-red-300 uppercase tracking-wide text-sm mb-1 flex items-center gap-2"><Swords size={16} /> Rival Alliance</h3>
      <p className="text-xs text-zinc-500 mb-3">Mark one alliance as your rival — your members earn <span className="text-red-300">+10% alliance ranking points</span> for defeating their gladiators in ranked challenges, and they show up red in the challenge list.</p>

      {a.rival_id ? (
        <div className="flex flex-wrap items-center gap-3 border border-red-800/40 bg-red-950/20 px-4 py-3" data-testid="current-rival">
          <Swords size={16} className="text-red-400" />
          <button onClick={() => navigate(`/alliance/${a.rival_id}`)} className="text-red-200 hover:text-red-100 font-cinzel tracking-wide">
            {a.rival_name || "Unknown alliance"} {a.rival_tag && <span className="text-red-500/70 text-sm">[{a.rival_tag}]</span>}
          </button>
          {isLeader && (
            <button data-testid="remove-rival-btn" disabled={busy} onClick={() => setRival(null)}
              className="ml-auto py-1.5 px-3 text-xs border border-zinc-700 text-zinc-300 hover:border-zinc-500 rounded-sm transition-colors">Remove rival</button>
          )}
        </div>
      ) : isLeader ? (
        <div className="relative max-w-md">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input data-testid="rival-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search an alliance to rival…"
            className="w-full bg-zinc-900 border border-zinc-800 pl-9 pr-3 py-2.5 text-zinc-100 text-sm focus:border-red-600 outline-none" />
          {results && (
            <div className="mt-2 space-y-1 border border-zinc-800 divide-y divide-zinc-900" data-testid="rival-results">
              {results.length === 0 ? <p className="text-[11px] text-zinc-600 px-3 py-2">No alliances found.</p> : results.map((r) => (
                <div key={r.id} className="flex items-center gap-2 text-sm px-3 py-2">
                  <span className="text-zinc-200 truncate">{r.name} {r.tag && <span className="text-zinc-600 text-xs">[{r.tag}]</span>}</span>
                  <button data-testid={`set-rival-${r.id}`} disabled={busy} onClick={() => setRival(r.id)}
                    className="ml-auto text-xs text-red-300 hover:text-red-200 flex items-center gap-1"><Swords size={13} /> Rival</button>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <p className="text-xs text-zinc-600" data-testid="no-rival-note">Your alliance hasn't declared a rival. Only the Dominus can declare one.</p>
      )}
    </section>
  );
}
